/**
 * Session security helpers.
 *
 * - The master-login session (localStorage `masterUser`) previously had NO
 *   expiry; it now carries an absolute expiry and is auto-cleared once stale.
 * - Supabase Auth manages its own access-token expiry + refresh.
 * - The Super Admin panel uses a signed, 8h sessionStorage token (cleared on
 *   tab close).
 *
 * Reduced exposure: only a minimal user object is stored (no password/secret),
 * and stale data is removed automatically rather than lingering in localStorage.
 */

const MASTER_KEY = 'masterUser';
const STAFF_KEY = 'staffUser';
const DEFAULT_TTL_MS = 12 * 60 * 60 * 1000; // 12 hours absolute

/** Safely decode JWT payload without external libraries */
export function parseJwt(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (_) {
    return null;
  }
}

/** Check if a JWT token is expired (or close to expiring within thresholdSeconds) */
export function isJwtExpired(token, thresholdSeconds = 30) {
  const parsed = parseJwt(token);
  if (!parsed || !parsed.exp) return true; // Invalid token format treated as expired
  const now = Math.floor(Date.now() / 1000);
  return parsed.exp - now <= thresholdSeconds;
}

/** Store the master session with an absolute expiry. */
export function setMasterSession(user, ttlMs = DEFAULT_TTL_MS) {
  try {
    console.log('[AUTH] Storage Write: Setting masterUser session');
    const minimal = {
      uid: user?.uid,
      email: user?.email,
      displayName: user?.displayName,
      isMaster: !!user?.isMaster,
      token: user?.token || null,
    };
    localStorage.setItem(MASTER_KEY, JSON.stringify({ user: minimal, expiresAt: Date.now() + ttlMs }));
  } catch (err) {
    console.error('[AUTH] Failed to write master session:', err);
  }
}

/** Return the master user if the session is valid, else null (auto-clears stale). */
export function getMasterSession() {
  let raw;
  try {
    raw = localStorage.getItem(MASTER_KEY);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  try {
    const rec = JSON.parse(raw);
    if (rec && rec.user && typeof rec.expiresAt === 'number') {
      if (Date.now() > rec.expiresAt) {
        console.log('[AUTH] Token Expired: masterUser session past TTL');
        clearMasterSession();
        return null;
      }
      return rec.user;
    }
    // Legacy raw-user format (no expiry) — wrap with a fresh TTL once
    if (rec && (rec.uid || rec.isMaster)) {
      setMasterSession(rec);
      return rec;
    }
  } catch (err) {
    console.error('[AUTH] Failed to parse master user session:', err);
  }
  clearMasterSession();
  return null;
}

export function clearMasterSession() {
  try {
    console.log('[AUTH] Storage Read/Write: Removing masterUser from localStorage');
    localStorage.removeItem(MASTER_KEY);
  } catch (_) {}
}

/** Retrieve and validate staff user session from localStorage */
export function getStaffSession() {
  let raw;
  try {
    raw = localStorage.getItem(STAFF_KEY);
  } catch (_) {
    return null;
  }
  if (!raw) return null;
  try {
    const su = JSON.parse(raw);
    if (su && su.staffId && su.agencyId) {
      if (su.token && isJwtExpired(su.token)) {
        console.log('[AUTH] Token Expired: staffUser token is expired');
        clearStaffSession();
        return null;
      }
      return su;
    }
  } catch (err) {
    console.error('[AUTH] Failed to parse staff user session:', err);
  }
  clearStaffSession();
  return null;
}

export function clearStaffSession() {
  try {
    console.log('[AUTH] Storage Read/Write: Removing staffUser from localStorage');
    localStorage.removeItem(STAFF_KEY);
  } catch (_) {}
}

/** Clear ONLY invalid authentication storage entries without wiping user app settings */
export function clearInvalidAuthStorage() {
  console.log('[AUTH] Clearing invalid auth storage keys (preserving app settings)');
  clearMasterSession();
  clearStaffSession();

  try {
    sessionStorage.removeItem('sa_authed');
    sessionStorage.removeItem('sa_token');
    sessionStorage.removeItem('sa_coadmin');
  } catch (_) {}

  // Remove Supabase auth keys from localStorage (e.g., sb-*-auth-token)
  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        console.log(`[AUTH] Storage Read/Write: Removing Supabase token key ${key}`);
        localStorage.removeItem(key);
      }
    });
  } catch (err) {
    console.error('[AUTH] Error cleaning Supabase storage keys:', err);
  }
}

/** Clear every app session artifact (master + staff + super-admin). */
export function clearAllSessions() {
  clearInvalidAuthStorage();
}

